import { useState, useContext, useEffect } from 'react';
import {
  useMediaQuery,
  useTheme,
  Grid,
  Typography,
  Button,
  Box,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import BenchRepairForm from './depot/benchRepair/benchRepairForm';
import RltWorklogForm from './depot/rltWorklogForm';
import IncorrectCaseForm from './fst/incorrectCase/incorrectCaseForm';
import OvertimeForm from './fst/overtime/overtimeForm';
import CaliforniaPtlForm from './nrc/californiaPtl/californiaPtlForm';
import ConsumableSupplyForm from './nrc/consumableSupply/consumableSupplyForm';
import ConsumableSupplyNotificationForm from './nrc/consumableSupplyNotification/consumableSupplyNotificationForm';
import DispatchStatusChangeForm from './nrc/dispatchStatusChange/dispatchStatusChangeForm';
import ForwardToSiteForm from './nrc/forwardToSite/forwardToSiteForm';
import GNIEUpdateRequestForm from './nrc/gnieUpdateRequest/gnieUpdateRequestForm';
import AntillesCreditLogForm from './nrc/antillesCreditLog/antillesCreditLogForm';
import MichiganIncidentLogForm from './nrc/michiganIncidentLog/michiganIncidentLogForm';
import NJSuppressedForm from './nrc/njSuppressed/njSuppressedForm';
import NYAMLogForm from './nrc/nyAMLog/nyAMLogForm';
import NYKeyRequestForm from './nrc/nyKeyRequest/nyKeyRequestForm';
import NRCDowntimeForm from './nrc/nrcDowntime/nrcDowntimeForm';
import RetailerInfoChangeForm from './nrc/retailerInfoChange/retailerInfoChangeForm';
import StolenAndMissingInstantTicketForm from './nrc/stolenAndMissingInstantTicket/stolenAndMissingInstantTicketForm';
import TexasScratchTicketForm from './nrc/texasScratchTicket/texasScratchTicketForm';
import WARetailerTrainingRequestForm from './nrc/waRetailerTrainingRequest/waRetailerTrainingRequestForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { formMenuData } from '../../../data/forms/formMenuData';
import { LanguageContext } from '../../../context/languageContext';
import { useAuth } from '../../../context/authContext';
import { useCopilotChat } from '../../copilot/copilotChatContext';

const Forms = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translations } = useContext(LanguageContext);
  const queryParams = new URLSearchParams(location.search);
  const [showForm, setShowForm] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    queryParams.get('country') || 'United States'
  );
  const [selectedTeam, setSelectedTeam] = useState(
    queryParams.get('team') || ''
  );
  const [selectedFormType, setSelectedFormType] = useState(
    queryParams.get('form') || ''
  );
  const { user } = useAuth();
  const { isOpen: isCopilotOpen, panelWidth } = useCopilotChat();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCountry) params.set('country', selectedCountry);
    if (selectedTeam) params.set('team', selectedTeam);
    if (selectedFormType) params.set('report', selectedFormType);
    navigate({ search: params.toString() });
  }, [selectedCountry, selectedTeam, selectedFormType, navigate]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const teams =
    formMenuData(translations).find(form => form.country === selectedCountry)
      ?.teams || [];
  const forms = teams.find(team => team.name === selectedTeam)?.forms || [];

  const desktopFormsWidth = isCopilotOpen
    ? `min(90vw, calc(100vw - ${panelWidth + 56}px))`
    : '90vw';

  const handleCountryChange = event => {
    setSelectedCountry(event.target.value);
    setSelectedTeam('');
    setSelectedFormType('');
    setShowForm(false);
  };

  const handleTeamChange = event => {
    setSelectedTeam(event.target.value);
    setSelectedFormType('');
    setShowForm(false);
  };

  const handleFormTypeChange = event => {
    setSelectedFormType(event.target.value);
    setShowForm(false);
  };

  const handleShowFormClick = () => {
    if (selectedFormType) {
      setIsMenuVisible(false);
      setShowForm(true);
    }
  };

  const handleFormSelectionReset = () => {
    setSelectedTeam('');
    setSelectedFormType('');
    setShowForm(false);
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'var(--reach-blue)',
          height: '35px',
          display: 'flex',
          alignItems: 'center',
          marginBottom: '50px',
        }}
      >
        <Typography
          sx={{ color: '#FFFFFF', fontFamily: 'BrightstarSans-Bold', ml: 2 }}
        >
          {translations.forms || 'Forms'}
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          margin: '20px',
          justifyItems: 'center',
          '--forms-layout-width': desktopFormsWidth,
        }}
      >
        <Grid container spacing={2} sx={{ marginBottom: '25px' }}>
          {isMobile && (
            <Grid size={{ xs: 12 }}>
              <Button
                variant='outlined'
                onClick={() => setIsMenuVisible(!isMenuVisible)}
                sx={{
                  mb: 2,
                  flex: 1,
                  color: '#ffffff',
                  backgroundColor: isMenuVisible
                    ? 'var(--reach-blue)'
                    : 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                }}
              >
                {isMenuVisible
                  ? translations.hideFilters || 'Hide Filters'
                  : translations.selectFilters || 'Select Filters'}
              </Button>
            </Grid>
          )}
          {(!isMobile || isMenuVisible) && (
            <Grid size={{ xs: 12, md: 4 }} order={{ xs: 1, md: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', md: 'row' },
                  width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormControl
                    required
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                      width: '100%',
                    }}
                  >
                    <InputLabel>
                      {translations.selectCountry || 'Select Country'}
                    </InputLabel>
                    <Select
                      value={selectedCountry}
                      label={translations.selectCountry || 'Select Country'}
                      onChange={handleCountryChange}
                    >
                      {formMenuData(translations).map(form => (
                        <MenuItem value={form.country}>{form.country}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl
                    required
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                      width: '100%',
                    }}
                    disabled={!selectedCountry}
                  >
                    <InputLabel>
                      {translations.selectTeam || 'Select Team'}
                    </InputLabel>
                    <Select
                      value={selectedTeam}
                      label={translations.selectTeam || 'Select Team'}
                      onChange={handleTeamChange}
                    >
                      {teams.map(form => (
                        <MenuItem value={form.name}>{form.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl
                    required
                    sx={{
                      backgroundColor: '#ffffff',
                      marginBottom: '15px',
                      width: '100%',
                    }}
                    disabled={!selectedTeam}
                  >
                    <InputLabel>
                      {translations.selectFormType || 'Select Form Type'}
                    </InputLabel>
                    <Select
                      value={selectedFormType}
                      label={translations.selectFormType || 'Select Form Type'}
                      onChange={handleFormTypeChange}
                    >
                      {forms.map(form => (
                        <MenuItem value={form.componentKey}>
                          {form.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  height: 'auto',
                  marginBottom: '50px',
                  width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
                }}
              >
                <Button
                  variant='outlined'
                  onClick={handleShowFormClick}
                  disabled={!selectedFormType}
                  sx={{
                    backgroundColor: selectedFormType
                      ? 'var(--canopus-violet)'
                      : '',
                    color: '#ffffff',
                    fontFamily: 'BrightstarSans-Regular',
                    width: { xs: '70%', md: '25%' },
                  }}
                >
                  {translations.loadForm || 'Load Form'}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 8 }} order={{ xs: 2, md: 1 }}>
          {/** DEPOT */}
          {showForm && selectedFormType === 'BenchRepairForm' && (
            <BenchRepairForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'RltWorklogForm' && (
            <RltWorklogForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}

          {/** FST */}
          {showForm && selectedFormType === 'IncorrectCaseForm' && (
            <IncorrectCaseForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'FSTOvertimeForm' && (
            <OvertimeForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}

          {/** NRC */}
          {showForm && selectedFormType === 'CaliforniaPTLForm' && (
            <CaliforniaPtlForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'ConsumableSupplyForm' && (
            <ConsumableSupplyForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm &&
            selectedFormType === 'ConsumableSupplyNotificationForm' && (
              <ConsumableSupplyNotificationForm
                translations={translations}
                user={user}
                handleFormSelectionReset={handleFormSelectionReset}
                setSnackbarMessage={setSnackbarMessage}
                setIsSnackbarOpen={setIsSnackbarOpen}
                setSnackbarSeverity={setSnackbarSeverity}
              />
            )}
          {showForm &&
            selectedFormType === 'DispatchStatusChangeRequestForm' && (
              <DispatchStatusChangeForm
                translations={translations}
                user={user}
                handleFormSelectionReset={handleFormSelectionReset}
                setSnackbarMessage={setSnackbarMessage}
                setIsSnackbarOpen={setIsSnackbarOpen}
                setSnackbarSeverity={setSnackbarSeverity}
              />
            )}
          {showForm && selectedFormType === 'ForwardToSiteForm' && (
            <ForwardToSiteForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'GNIEUpdateRequestForm' && (
            <GNIEUpdateRequestForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'AntillesCreditLogForm' && (
            <AntillesCreditLogForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'MichiganIncidentLogForm' && (
            <MichiganIncidentLogForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'NJSuppressedForm' && (
            <NJSuppressedForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'NYAMLogForm' && (
            <NYAMLogForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'NYKeyRequestForm' && (
            <NYKeyRequestForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'NRCDowntimeForm' && (
            <NRCDowntimeForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'RetailerInfoChangeForm' && (
            <RetailerInfoChangeForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm &&
            selectedFormType === 'StolenAndMissingInstantTicketForm' && (
              <StolenAndMissingInstantTicketForm
                translations={translations}
                user={user}
                handleFormSelectionReset={handleFormSelectionReset}
                setSnackbarMessage={setSnackbarMessage}
                setIsSnackbarOpen={setIsSnackbarOpen}
                setSnackbarSeverity={setSnackbarSeverity}
              />
            )}
          {showForm && selectedFormType === 'TexasScratchTicketForm' && (
            <TexasScratchTicketForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
          {showForm && selectedFormType === 'WARetailerTrainingRequestForm' && (
            <WARetailerTrainingRequestForm
              translations={translations}
              user={user}
              handleFormSelectionReset={handleFormSelectionReset}
              setSnackbarMessage={setSnackbarMessage}
              setIsSnackbarOpen={setIsSnackbarOpen}
              setSnackbarSeverity={setSnackbarSeverity}
            />
          )}
        </Grid>
      </Box>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Forms;
